import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/vqfz3t07_ENFLEET%20by%20POMEN.png";

function PasswordField({ label, value, onChange, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <Input data-testid={testId} type={show ? "text" : "password"} required minLength={6} value={value}
          className="pr-11" onChange={onChange} />
        <button type="button" tabIndex={-1} onClick={() => setShow(!show)} data-testid={`${testId}-toggle`}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#10056E]"
          aria-label={show ? "Sembunyi kata laluan" : "Lihat kata laluan"}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function ActivateAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadInfo = useCallback(async () => {
    if (!token) { setInvalid("Pautan tidak sah — tiada token ditemui."); return; }
    try {
      const { data } = await api.get(`/auth/activation/${token}`);
      setInfo(data);
    } catch (err) {
      setInvalid(apiError(err));
    }
  }, [token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Kata laluan tidak sepadan");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/activate", { token, password });
      toast.success("Akaun diaktifkan. Selamat datang!");
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#10056E] flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-40 right-1/4 w-[550px] h-[550px] rounded-full bg-[#2a17a8]/40 blur-3xl" />
      </div>
      <div className="relative z-10 w-full max-w-md fade-up">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="ENFLEET by POMEN" className="h-14 w-auto" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-7 sm:p-8" data-testid="activate-page">
          <h2 className="font-heading text-xl font-semibold tracking-tight mb-1">Aktifkan Akaun Anda</h2>
          {invalid ? (
            <div className="space-y-4 mt-4">
              <p className="text-sm bg-red-50 border border-red-200 text-[#DC3545] rounded-lg p-3" data-testid="activate-invalid-msg">{invalid}</p>
              <Button variant="outline" className="w-full" onClick={() => navigate("/login")} data-testid="activate-goto-login-btn">
                Pergi ke Log Masuk
              </Button>
            </div>
          ) : !info ? (
            <p className="text-sm text-muted-foreground mt-4">Menyemak pautan pengaktifan...</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Hai <b>{info.name}</b>, tetapkan kata laluan untuk akaun <b>{info.email}</b> (min. 6 aksara).
              </p>
              <form className="space-y-4" onSubmit={submit}>
                <PasswordField label="Kata Laluan Baharu" value={password} testId="activate-password-input"
                  onChange={(e) => setPassword(e.target.value)} />
                <PasswordField label="Sahkan Kata Laluan" value={confirm} testId="activate-confirm-input"
                  onChange={(e) => setConfirm(e.target.value)} />
                {error && <p className="text-sm text-[#DC3545]" data-testid="activate-error">{error}</p>}
                <Button type="submit" className="w-full bg-[#10056E] hover:bg-[#2a17a8]" disabled={busy} data-testid="activate-submit-btn">
                  {busy ? "Mengaktifkan..." : "Aktifkan & Log Masuk"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
